import type { Ref, WatchSource } from 'vue';
import type { LocationQuery, RouteLocationNormalizedLoaded, Router } from 'vue-router';

import type { DataTableOptions } from './useDataTableDefaults';
import type {
  ListRouteQueryRecord,
  ListRouteQueryParams,
  ListRouteStateCodec,
} from './useListRouteState';
import { createListRouteStateCodec, useListRouteState } from './useListRouteState';
import type { Pagination } from '@/scripts/Common/paginationType';

export type DataTableRouteState = {
  page: number;
  pageSize: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
};

export interface UseDataTableRouteStateOptions<TFilterState extends object> {
  route: RouteLocationNormalizedLoaded;
  router: Router;
  pagination?: Ref<Pagination>;
  dataTableOptions?: Ref<DataTableOptions>;
  tableState?: DataTableRouteTableState;
  filterParams: ListRouteQueryParams<TFilterState>;
  filterState?: Ref<TFilterState>;
  getFilterState?: () => TFilterState;
  applyFilterState?: (state: TFilterState) => void;
  watchSources?: WatchSource[];
  defaultPageSize?: number;
}

export interface DataTableRouteTableState {
  getState: () => DataTableRouteState;
  applyState: (state: DataTableRouteState) => void;
  watchSources: WatchSource[];
}

const createDefaultTableState = (
  pagination: Ref<Pagination>,
  dataTableOptions: Ref<DataTableOptions>,
): DataTableRouteTableState => ({
  getState: () => ({
    page: pagination.value.page,
    pageSize: pagination.value.itemsPerPage,
    sortColumn: dataTableOptions.value.sorting.sortBy?.[0]?.key,
    sortDirection: dataTableOptions.value.sorting.sortBy?.[0]?.order,
  }),
  applyState: state => {
    pagination.value.page = state.page;
    pagination.value.itemsPerPage = state.pageSize;
    dataTableOptions.value.sorting.sortBy = state.sortColumn
      ? [{ key: state.sortColumn, order: state.sortDirection ?? 'asc' }]
      : undefined;
  },
  watchSources: [
    () => pagination.value.page,
    () => pagination.value.itemsPerPage,
    () => dataTableOptions.value.sorting,
  ],
});

const parseTableState = (
  query: LocationQuery,
  defaultPageSize: number,
): DataTableRouteState => {
  const page = Number(query.page);
  const pageSize = Number(query.pageSize);
  const sortDirection = query.sortDirection;

  return {
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: Number.isInteger(pageSize) && pageSize > 0 ? pageSize : defaultPageSize,
    sortColumn: typeof query.sortColumn === 'string' ? query.sortColumn : undefined,
    sortDirection: sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : undefined,
  };
};

const serializeTableState = (
  state: DataTableRouteState,
  defaultPageSize: number,
): ListRouteQueryRecord => ({
  page: state.page === 1 ? undefined : state.page,
  pageSize: state.pageSize === defaultPageSize ? undefined : state.pageSize,
  sortColumn: state.sortColumn,
  sortDirection: state.sortDirection,
});

export const useDataTableRouteState = <TFilterState extends object>({
  route,
  router,
  pagination,
  dataTableOptions,
  tableState,
  filterParams,
  filterState,
  getFilterState,
  applyFilterState,
  watchSources = [],
  defaultPageSize = 10,
}: UseDataTableRouteStateOptions<TFilterState>) => {
  const resolvedTableState = tableState ?? (
    pagination && dataTableOptions
      ? createDefaultTableState(pagination, dataTableOptions)
      : undefined
  );

  if (!resolvedTableState) {
    throw new Error('Either tableState or pagination and dataTableOptions must be provided.');
  }

  const readFilterState = getFilterState ?? (filterState
    ? () => filterState.value
    : undefined);
  const writeFilterState = applyFilterState ?? (filterState
    ? (state: TFilterState) => { Object.assign(filterState.value, state); }
    : undefined);

  if (!readFilterState || !writeFilterState) {
    throw new Error('Either filterState or getFilterState and applyFilterState must be provided.');
  }

  const filterCodec = createListRouteStateCodec(filterParams);

  const extractFilterState = (state: DataTableRouteState & TFilterState): TFilterState => {
    const currentFilterState = readFilterState();
    const nextFilterState = {} as TFilterState;

    for (const key of Object.keys(currentFilterState) as (keyof TFilterState)[]) {
      nextFilterState[key] = state[key] as TFilterState[typeof key];
    }

    return nextFilterState;
  };

  return useListRouteState({
    route,
    router,
    codec: {
      parse: query => ({
        ...parseTableState(query, defaultPageSize),
        ...filterCodec.parse(query),
      }),
      serialize: state => ({
        ...serializeTableState(state, defaultPageSize),
        ...filterCodec.serialize(state),
      }),
    } as ListRouteStateCodec<DataTableRouteState & TFilterState>,
    getState: () => ({
      ...resolvedTableState.getState(),
      ...readFilterState(),
    }),
    applyState: state => {
      resolvedTableState.applyState(state);
      writeFilterState(extractFilterState(state));
    },
    watchSources: [
      ...watchSources,
      ...(filterState ? [() => filterState.value] : []),
      ...resolvedTableState.watchSources,
    ],
  });
};
